import React, { useState, useEffect, useMemo } from "react";
import {
  Table,
  Typography,
  Card,
  Layout,
  Button,
  Space,
  Tag,
  message,
  Select,
  Row,
  Col,
} from "antd";
import SendTenderReviewModal from "../common/SendTenderReviewModal";
import {
  ArrowLeftOutlined,
  EditOutlined,
  EyeOutlined,
  CalculatorOutlined,
  ImportOutlined,
  DollarCircleOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import MJ_LOGO from "@/assets/mnj-logo.jpg";
import AuthButton from "../auth/AuthButton";
import { useBillOfQuantities } from "../../hooks/useBillOfQuantities";
import { useScrollToTop } from "../../hooks/useScrollToTop";
import { billOfQuantitiesApi } from "../../api/billOfQuantities";
import { transformFromPayload } from "../../utils/storage";
import { useTender } from "../../contexts/TenderContext";
import { useUserRole } from "../../hooks/useUserRole";
// import { useChangeDetectionContext } from "../../contexts/ChangeDetectionContext";
import { getCurrentBoqVersion } from "../../utils/boqVersionUtils";
import { createVersionPayload } from "../../utils/boqPayloadBuilder";
import { getBoqVersionPrefixFromValue, getNumericBoqVersion } from "../../utils/boqVersionPrefix";
import { geoToberWallBuildUpApi } from "../../api/wallSectionBuildUp";
import { retainingWallsBuildUpApi } from "../../api/retainingWallsBuildUp";
import { useItemLibrary } from "../../hooks/useItemLibrary";
import { EnvironmentBadge } from "../common/EnvironmentBadge";
import { useDevMode } from "../../contexts/DevModeContext";
import { Layers3, Route } from "lucide-react";

const { Title } = Typography;
const { Header: AntHeader } = Layout;

interface VersionRecord {
  key: string;
  version: number | string;
  tenderId: string;
  tender: string;
  client: string;
  totalBudget: number;
  rsBudget: number;
  gwBudget: number;
  creationDate: string;
  lastUpdateDate: string;
  status: "In-Progress" | "Submitted" | "Approved";
  creator: string;
  lastModifiedBy: string;
  changeLog?: string;
  rateVersion?: number;
}

const StatTile: React.FC<{
  icon: React.ReactNode;
  color: string;
  label: string;
  value: string | number;
  hint?: string;
}> = ({ icon, color, label, value, hint }) => (
  <div
    style={{
      flex: "1 1 220px",
      display: "flex",
      alignItems: "center",
      gap: 14,
      padding: "16px 20px",
      background: "#fff",
      borderRadius: 12,
      border: "1px solid #f0f0f0",
      boxShadow: "0 1px 2px rgba(0,0,0,0.04), 0 1px 6px -1px rgba(0,0,0,0.06)",
    }}
  >
    <div
      style={{
        width: 44,
        height: 44,
        flexShrink: 0,
        borderRadius: 10,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 20,
        color,
        background: `${color}1a`,
      }}
    >
      {icon}
    </div>
    <div style={{ minWidth: 0 }}>
      <div
        style={{
          fontSize: 12,
          fontWeight: 600,
          color: "#8c8c8c",
          textTransform: "uppercase",
          letterSpacing: 0.4,
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, color: "#1f1f1f", lineHeight: 1.4 }}>
        {value}
      </div>
      {hint && (
        <div style={{ fontSize: 11, color: "#bfbfbf", marginTop: 2 }}>{hint}</div>
      )}
    </div>
  </div>
);

const VersionHistory: React.FC = () => {
  // Scroll to top on page load
  useScrollToTop();

  const navigate = useNavigate();
  const { state: tenderState, dispatch, setViewMode } = useTender();

  // Pre-fetch the activity item library for the loaded tender's region and site so it is
  // warm in the React Query cache when the user enters the tender calculation flow.
  // Data is consumed downstream by StepperNavigation, GroundworksReview, etc.
  useItemLibrary(tenderState.region || '', tenderState.projectName || undefined);
  // const { clearAllChanges } = useChangeDetectionContext();
  const { projectName } = useParams<{ projectName: string }>();
  const decodedProjectName = projectName ? decodeURIComponent(projectName) : "";
  const [searchParams] = useSearchParams();
  const oppNo = searchParams.get("oppNo") || undefined;
  const [versionData, setVersionData] = useState<VersionRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [jobTypeFilter, setJobTypeFilter] = useState<string>("all");
  const { role: userRole } = useUserRole();
  const devMode = useDevMode();
  const [isEditConfirmModalVisible, setIsEditConfirmModalVisible] = useState(false);
  const [editConfirmRecord, setEditConfirmRecord] = useState<VersionRecord | null>(null);
  const [isCreatingVersion, setIsCreatingVersion] = useState(false);

  // Fetch Bill of Quantities data using the hook
  const {
    data: boqData,
    isLoading: boqLoading,
    error: boqError,
  } = useBillOfQuantities();


  // Clear change detection context when version history loads
  /* useEffect(() => {
    clearAllChanges();
  }, [clearAllChanges]); */

  // Load version data when component mounts or BOQ data changes
  useEffect(() => {
    loadVersionData();
  }, [boqData, decodedProjectName]);

  // Fallback function to load current BOQ data as version 1
  const loadCurrentBoqAsVersion1 = async () => {
    try {
      const response =
        await billOfQuantitiesApi.getByProject(decodedProjectName, oppNo);

      let apiData = null;
      if (response && response.success !== false) {
        if (response.data) {
          apiData = response.data;
        } else if (response.project) {
          apiData = response;
        } else if (
          response &&
          typeof response === "object" &&
          Object.keys(response).length > 0
        ) {
          apiData = response;
        }
      }

      if (apiData) {
        // Calculate budgets
        let rsBudget = 0;
        let gwBudget = 0;

        if (apiData.billOfQuantities && apiData.billOfQuantities.length > 0) {
          const boq = apiData.billOfQuantities[0];
          rsBudget = boq.roadAndSewers?.totals?.grandTotal || 0;
          gwBudget = boq.groundworks?.totals?.grandTotal || 0;
        }

        const totalBudget = apiData.totals?.tenderTotal || rsBudget + gwBudget;

        // Create version 1 record from current BOQ
        const versionRecord: VersionRecord = {
          key: "version_1",
          version: 1,
          tenderId: apiData._id || "unknown",
          tender: decodedProjectName,
          client: apiData.project?.client || "-",
          totalBudget,
          rsBudget,
          gwBudget,
          creationDate:
            apiData.tenderCreatedDate ||
            apiData.calculatedAt ||
            new Date().toISOString(),
          lastUpdateDate:
            apiData.lastModified ||
            apiData.calculatedAt ||
            new Date().toISOString(),
          status: apiData.boqStatus || apiData.status || "Pending",
          creator: apiData.createdBy || "Unknown",
          lastModifiedBy: apiData.lastModifiedBy || "-",
          changeLog: "Current BOQ data (no version history available)",
          rateVersion: 1,
        };

        setVersionData([versionRecord]);
      } else {
        setVersionData([]);
        message.warning("No data found for this project");
      }
    } catch (error: any) {
      setVersionData([]);
      message.warning("No data found for this project");
    }
  };

  const loadVersionData = async () => {
    setLoading(true);
    try {
      if (!decodedProjectName) {
        setVersionData([]);
        setLoading(false);
        return;
      }


      // Fetch version history from new API endpoint
      const versionHistoryResponse =
        await billOfQuantitiesApi.getVersionHistory(decodedProjectName, oppNo);


      if (
        versionHistoryResponse &&
        versionHistoryResponse.versions &&
        Array.isArray(versionHistoryResponse.versions) &&
        versionHistoryResponse.versions.length > 0
      ) {
        // Map API response to VersionRecord format
        const mappedVersions: VersionRecord[] =
          versionHistoryResponse.versions.map((version: any) => {
            return {
              key: `version_${version.version}`,
              version: version.version,
              tenderId: `${decodedProjectName}_v${version.version}`,
              tender: decodedProjectName,
              client: versionHistoryResponse.clientName || decodedProjectName,
              totalBudget: version.snapshot?.tenderTotal || 0,
              rsBudget: version.snapshot?.roadAndSewersTotal || 0,
              gwBudget: version.snapshot?.groundworksTotal || 0,
              creationDate: version.createdAt,
              lastUpdateDate: version.lastModified || version.createdAt,
              status: version.snapshot?.boqStatus || version.snapshot?.status || "Pending",
              creator: version.createdBy || "Unknown",
              lastModifiedBy: version.snapshot?.lastModifiedBy || "-",
              changeLog: version.changeLog || "",
              rateVersion: version.rateVersion || 1,
            };
          });

        // Sort by version descending (newest first)
        mappedVersions.sort((a, b) => getNumericBoqVersion(b.version) - getNumericBoqVersion(a.version));

        setVersionData(mappedVersions);
      } else {
        // No version history found - fallback to current BOQ data as version 1
        await loadCurrentBoqAsVersion1();
      }
    } catch (error: any) {
      // Fallback to current BOQ data on error
      try {
        await loadCurrentBoqAsVersion1();
      } catch (fallbackError) {
        message.error("Failed to load project data");
        setVersionData([]);
      }
    } finally {
      setLoading(false);
    }
  };

  // Edit action - navigates based on budget
  const handleEdit = async (record: VersionRecord, navigationPath?: string) => {
    try {
      message.loading({ content: "Loading tender data...", key: "loadTender", duration: 0 });


      // Check if this is a fallback record (no real version history)
      let isFallbackRecord =
        record.changeLog ===
          "Current BOQ data (no version history available)" ||
        (record.version === 1 && versionData.length === 1);

      let response;
      if (isFallbackRecord) {
        // Load from BOQ API directly (no version history)
        response = await billOfQuantitiesApi.getByProject(record.tender, oppNo);
      } else {
        // Load specific version from version history
        try {
          response = await billOfQuantitiesApi.getBoqVersion(
            record.tender,
            record.version,
            oppNo,
          );
        } catch (versionError: any) {
          // If version not found (404), fall back to loading from BOQ API directly
          if (versionError?.response?.status === 404) {
            response = await billOfQuantitiesApi.getByProject(record.tender, oppNo);
            isFallbackRecord = true;
          } else {
            throw versionError;
          }
        }
      }


      let apiData = null;
      if (response && response.success === false) {
        message.error({
          content: response.message || "Failed to load tender data",
          key: "loadTender",
          duration: 3,
        });
        return;
      }

      // Extract BOQ data from the response
      if (isFallbackRecord) {
        // For BOQ API response
        if (response && response.data) {
          apiData = response.data;
        } else if (response && response.project) {
          apiData = response;
        } else if (
          response &&
          typeof response === "object" &&
          Object.keys(response).length > 0
        ) {
          apiData = response;
        }
      } else {
        // For version API response - extract from 'boq' key
        if (response && response.boq) {
          apiData = response.boq;
        } else if (response && response.data) {
          apiData = response.data;
        } else if (response && response.project) {
          apiData = response;
        } else if (
          response &&
          typeof response === "object" &&
          Object.keys(response).length > 0
        ) {
          apiData = response;
        }
      }

      if (!apiData) {
        message.error({
          content: "No tender data found",
          key: "loadTender",
          duration: 3,
        });
        return;
      }

      const tenderState = transformFromPayload(apiData, {
        id: record.tenderId,
      });

      let hasRoadsAndSewersData = false;
      let hasGroundworksData = false;

      if (
        apiData.roadAndSewers ||
        apiData.preliminaryItems ||
        apiData.foulSewers ||
        apiData.stormSewers ||
        apiData.roads ||
        apiData.mainServices
      ) {
        hasRoadsAndSewersData = true;
      }

      if (
        apiData.groundworks ||
        (apiData.cutFills && Object.keys(apiData.cutFills).length > 0) ||
        (apiData.foundations && Object.keys(apiData.foundations).length > 0)
      ) {
        hasGroundworksData = true;
      }

      const apiJobTypesEdit = (apiData as Record<string, unknown>)?.['job-types'] as { groundworks?: boolean; roadsAndSewers?: boolean } | undefined;
      const hasExplicitFlagsEdit = apiJobTypesEdit?.groundworks !== undefined || apiJobTypesEdit?.roadsAndSewers !== undefined;
      tenderState.jobTypes = hasExplicitFlagsEdit
        ? { roadsAndSewers: apiJobTypesEdit?.roadsAndSewers === true, groundworks: apiJobTypesEdit?.groundworks === true }
        : { roadsAndSewers: hasRoadsAndSewersData || record.rsBudget > 0, groundworks: hasGroundworksData || record.gwBudget > 0 };

      // IMPORTANT: Save to localStorage BEFORE dispatching events
      // This ensures that when versionDataReloaded event fires, localStorage has the new data
      const storageKey = `mjEvans_tender_${record.tenderId}`;
      const dataToStore = {
        version: "3.0",
        timestamp: new Date().toISOString(),
        data: apiData,
        versionInfo: {
          version: record.version,
          projectName: record.tender,
        },
      };

      localStorage.setItem(storageKey, JSON.stringify(dataToStore));
      localStorage.setItem("currentTender", JSON.stringify(dataToStore));
      localStorage.setItem(
        "currentBoqVersion",
        JSON.stringify({
          version: record.version,
          projectName: record.tender,
        }),
      );

      // Now dispatch to context - localStorage is already updated
      dispatch({ type: "LOAD_PROJECT", payload: tenderState });




      message.success({
        content: "Tender data loaded successfully",
        key: "loadTender",
        duration: 2,
      });

      // Set view mode to false - editing mode enables all inputs
      setViewMode(false);

      // Store in localStorage that this is NOT a historical version (editable)
      localStorage.setItem("isHistoricalVersion", "false");

      // Navigate: use explicit path if provided, otherwise pick based on this
      // record's own GWKS-/RS- prefix (not tenderState.jobTypes, which is the
      // whole tender's flags and would always resolve the same way regardless
      // of which job type's row was actually clicked on a dual-job-type tender).
      // Legacy/ambiguous (no prefix) records fall back to the old jobTypes check.
      const recordPrefix = getBoqVersionPrefixFromValue(record.version);
      const editNavPath = navigationPath ?? (
        recordPrefix === "RS"
          ? "/roads-sewers/preliminary"
          : recordPrefix === "GWKS"
            ? "/groundworks/cut-fills"
            : tenderState.jobTypes.roadsAndSewers && !tenderState.jobTypes.groundworks
              ? "/roads-sewers/preliminary"
              : "/groundworks/cut-fills"
      );
      navigate(editNavPath);
    } catch (error) {
      message.error({
        content: "Failed to load tender data",
        key: "loadTender",
        duration: 3,
      });
    }
  };

  // Edit click - show confirmation modal if not In-Progress, otherwise navigate directly
  const handleEditClick = (record: VersionRecord) => {
    const statusLower = (record.status || "").toLowerCase().trim();
    if (statusLower === "in-progress") {
      handleEdit(record);
    } else {
      setEditConfirmRecord(record);
      setIsEditConfirmModalVisible(true);
    }
  };

  // Confirmed: create new version, set In-Progress, copy wall build-ups, navigate
  const handleEditConfirm = async () => {
    if (!editConfirmRecord) return;
    setIsCreatingVersion(true);
    setIsEditConfirmModalVisible(false);
    try {
      message.loading({ content: "Creating new version...", key: "createVersion", duration: 0 });

      // Step 1: Load current BOQ data for this version
      let isFallbackRecord =
        editConfirmRecord.changeLog === "Current BOQ data (no version history available)" ||
        (editConfirmRecord.version === 1 && versionData.length === 1);

      let response;
      if (isFallbackRecord) {
        response = await billOfQuantitiesApi.getByProject(editConfirmRecord.tender, oppNo);
      } else {
        try {
          response = await billOfQuantitiesApi.getBoqVersion(
            editConfirmRecord.tender,
            editConfirmRecord.version,
            oppNo,
          );
        } catch (versionError: any) {
          if (versionError?.response?.status === 404) {
            response = await billOfQuantitiesApi.getByProject(editConfirmRecord.tender, oppNo);
            isFallbackRecord = true;
          } else {
            throw versionError;
          }
        }
      }

      const responseAny = response as any;
      if (!responseAny || responseAny.success === false) {
        message.error({ content: "Failed to load tender data", key: "createVersion", duration: 3 });
        return;
      }

      let apiData: any = null;
      if (isFallbackRecord) {
        apiData = responseAny.data || (responseAny.project ? responseAny : null) || responseAny;
      } else {
        apiData = responseAny.boq || responseAny.data || (responseAny.project ? responseAny : null) || responseAny;
      }

      if (!apiData) {
        message.error({ content: "No tender data found", key: "createVersion", duration: 3 });
        return;
      }

      // Step 2: Update main BOQ status to In-Progress (POST to /billofquantities)
      const updatedApiData = { ...apiData, status: "In-Progress", boqStatus: "In-Progress" };
      try {
        await billOfQuantitiesApi.create(updatedApiData as unknown as Parameters<typeof billOfQuantitiesApi.create>[0], oppNo);
      } catch (boqUpdateError) {
      }

      // Step 3: Determine next version number. This page isn't nested under
      // /roads-sewers or /groundworks, so there's no route to auto-detect the
      // RS-/GWKS- prefix from — derive it from this record's own version value
      // instead, and scope the "current version" lookup by that same prefix
      // (otherwise it mixes RS and GWKS entries together).
      const versionPrefix = getBoqVersionPrefixFromValue(editConfirmRecord.version);
      const currentVersionInfo = await getCurrentBoqVersion(editConfirmRecord.tender, oppNo, versionPrefix);
      const nextVersionNumber = currentVersionInfo ? currentVersionInfo.version + 1 : 1;
      const nextVersionKey = versionPrefix ? `${versionPrefix}-${nextVersionNumber}` : nextVersionNumber;

      // Step 4: Create new BOQ version with In-Progress status
      const versionPayload = createVersionPayload({
        projectName: editConfirmRecord.tender,
        boqPayload: updatedApiData,
        changeLog: `Status changed from ${editConfirmRecord.status} to In-Progress`,
        version: nextVersionKey,
      });

      await billOfQuantitiesApi.createVersion(versionPayload as unknown as Parameters<typeof billOfQuantitiesApi.createVersion>[0], oppNo);

      // Step 5: Copy wall build-up data from current version to the new version
      // Since we don't have the live tender state here, fetch existing build-up data and re-post for the new version number
      const currentVersion = getNumericBoqVersion(editConfirmRecord.version);
      const tenderName = editConfirmRecord.tender;
      const tenderId = editConfirmRecord.tenderId;

      await Promise.all([
        // Copy retaining walls build-up
        retainingWallsBuildUpApi.get(tenderName, currentVersion).then(async (retainingData) => {
          if (retainingData && retainingData.specifications && retainingData.specifications.length > 0) {
            await retainingWallsBuildUpApi.post({
              tenderId,
              tenderName,
              tenderVersion: nextVersionNumber,
              specifications: retainingData.specifications,
              ...(retainingData.config ? { config: retainingData.config } : {}),
            });
          }
        }).catch(() => {}),

        // Copy geo/tober wall build-up
        geoToberWallBuildUpApi.get(tenderName, currentVersion).then(async (geoData) => {
          if (geoData && geoData.specifications && geoData.specifications.length > 0) {
            await geoToberWallBuildUpApi.post({
              tenderId,
              tenderName,
              tenderVersion: nextVersionNumber,
              tobermoreColumnHeaders: geoData.tobermoreColumnHeaders ?? {},
              tobermoreColumnDimensions: geoData.tobermoreColumnDimensions ?? {},
              specifications: geoData.specifications,
            });
          }
        }).catch(() => {}),
      ]);

      message.success({ content: "New version created", key: "createVersion", duration: 2 });

      // Step 6: Load the new version for editing and navigate
      const newRecord: VersionRecord = {
        ...editConfirmRecord,
        version: nextVersionKey,
        status: "In-Progress",
        changeLog: `Status changed from ${editConfirmRecord.status} to In-Progress`,
      };

      await handleEdit(newRecord);
    } catch (error) {
      message.error({ content: "Failed to create new version", key: "createVersion", duration: 3 });
    } finally {
      setIsCreatingVersion(false);
    }
  };

  // View action - navigates to the review-table section (GWKS/RS) the row was clicked from
  const handleView = async (record: VersionRecord, section: "GWKS" | "RS") => {
    try {
      message.loading({ content: "Loading tender data...", key: "loadTender", duration: 0 });


      // Check if this is a fallback record (no real version history)
      let isFallbackRecord =
        record.changeLog ===
          "Current BOQ data (no version history available)" ||
        (record.version === 1 && versionData.length === 1);

      let response;
      if (isFallbackRecord) {
        // Load from BOQ API directly (no version history)
        response = await billOfQuantitiesApi.getByProject(record.tender, oppNo);
      } else {
        // Load specific version from version history
        try {
          response = await billOfQuantitiesApi.getBoqVersion(
            record.tender,
            record.version,
            oppNo,
          );
        } catch (versionError: any) {
          if (versionError?.response?.status === 404) {
            response = await billOfQuantitiesApi.getByProject(record.tender, oppNo);
            isFallbackRecord = true;
          } else {
            throw versionError;
          }
        }
      }


      let apiData = null;
      if (response && response.success === false) {
        message.error({
          content: response.message || "Failed to load tender data",
          key: "loadTender",
          duration: 3,
        });
        return;
      }

      // Extract BOQ data from the response
      if (isFallbackRecord) {
        // For BOQ API response
        if (response && response.data) {
          apiData = response.data;
        } else if (response && response.project) {
          apiData = response;
        } else if (
          response &&
          typeof response === "object" &&
          Object.keys(response).length > 0
        ) {
          apiData = response;
        }
      } else {
        // For version API response - extract from 'boq' key
        if (response && response.boq) {
          apiData = response.boq;
        } else if (response && response.data) {
          apiData = response.data;
        } else if (response && response.project) {
          apiData = response;
        } else if (
          response &&
          typeof response === "object" &&
          Object.keys(response).length > 0
        ) {
          apiData = response;
        }
      }

      if (!apiData) {
        message.error({
          content: "No tender data found",
          key: "loadTender",
          duration: 3,
        });
        return;
      }

      const tenderState = transformFromPayload(apiData, {
        id: record.tenderId,
      });

      let hasRoadsAndSewersData = false;
      let hasGroundworksData = false;

      if (
        apiData.roadAndSewers ||
        apiData.preliminaryItems ||
        apiData.foulSewers ||
        apiData.stormSewers ||
        apiData.roads ||
        apiData.mainServices
      ) {
        hasRoadsAndSewersData = true;
      }

      if (
        apiData.groundworks ||
        (apiData.cutFills && Object.keys(apiData.cutFills).length > 0) ||
        (apiData.foundations && Object.keys(apiData.foundations).length > 0)
      ) {
        hasGroundworksData = true;
      }

      const apiJobTypesView = (apiData as Record<string, unknown>)?.['job-types'] as { groundworks?: boolean; roadsAndSewers?: boolean } | undefined;
      // Explicit job-types flags in the payload are the source of truth.
      // Only fall back to data/budget detection for legacy payloads that pre-date
      // the job-types field — avoids treating an empty groundworks:{} shell as selected.
      const hasExplicitFlags = apiJobTypesView?.groundworks !== undefined || apiJobTypesView?.roadsAndSewers !== undefined;
      tenderState.jobTypes = hasExplicitFlags
        ? { roadsAndSewers: apiJobTypesView?.roadsAndSewers === true, groundworks: apiJobTypesView?.groundworks === true }
        : { roadsAndSewers: hasRoadsAndSewersData || record.rsBudget > 0, groundworks: hasGroundworksData || record.gwBudget > 0 };

      // Ensure opportunityNo from the URL is preserved on the loaded state so that
      // hooks on the review page (useBoqVersionHistory etc.) send it as a query param.
      if (oppNo) {
        tenderState.opportunityNo = oppNo;
        (apiData as Record<string, unknown>).opportunityNo = oppNo;
      }

      // IMPORTANT: Save to localStorage BEFORE dispatching events
      // This ensures that when versionDataReloaded event fires, localStorage has the new data
      const storageKey = `mjEvans_tender_${record.tenderId}`;
      const dataToStore = {
        version: "3.0",
        timestamp: new Date().toISOString(),
        data: apiData,
        versionInfo: {
          version: record.version,
          projectName: record.tender,
        },
      };

      localStorage.setItem(storageKey, JSON.stringify(dataToStore));
      localStorage.setItem("currentTender", JSON.stringify(dataToStore));
      localStorage.setItem(
        "currentBoqVersion",
        JSON.stringify({
          version: record.version,
          projectName: record.tender,
        }),
      );

      // Now dispatch to context - localStorage is already updated
      dispatch({ type: "LOAD_PROJECT", payload: tenderState });




      message.success({
        content: "Tender data loaded successfully",
        key: "loadTender",
        duration: 2,
      });

      // Set view mode to true - this will disable all inputs and hide edit/delete/save buttons
      setViewMode(true);

      // Store in localStorage that this IS a historical version (read-only)
      localStorage.setItem("isHistoricalVersion", "true");

      // Navigate to the section (GWKS/RS table) the View icon was clicked from
      if (section === "GWKS") {
        navigate("/groundworks/cut-fills?mode=view");
      } else {
        navigate("/roads-sewers/preliminary?mode=view");
      }
    } catch (error) {
      message.error({
        content: "Failed to load tender data",
        key: "loadTender",
        duration: 3,
      });
    }
  };

  // Review/Calculate action - navigates to the review-table section (GWKS/RS) the row was clicked from
  const handleReviewCalculate = async (record: VersionRecord, section: "GWKS" | "RS") => {
    try {
      message.loading({ content: "Loading tender data...", key: "loadTender", duration: 0 });


      // Check if status is "In-progress" - if not, should be view-only mode
      const statusLower = (record.status || "").toLowerCase().trim();
      const isInProgress = statusLower === "in-progress";


      // Check if this is a fallback record (no real version history)
      let isFallbackRecord =
        record.changeLog ===
          "Current BOQ data (no version history available)" ||
        (record.version === 1 && versionData.length === 1);

      let response;
      if (isFallbackRecord) {
        // Load from BOQ API directly (no version history)
        response = await billOfQuantitiesApi.getByProject(record.tender, oppNo);
      } else {
        // Load specific version from version history
        try {
          response = await billOfQuantitiesApi.getBoqVersion(
            record.tender,
            record.version,
            oppNo,
          );
        } catch (versionError: any) {
          if (versionError?.response?.status === 404) {
            response = await billOfQuantitiesApi.getByProject(record.tender, oppNo);
            isFallbackRecord = true;
          } else {
            throw versionError;
          }
        }
      }


      let apiData = null;
      if (response && response.success === false) {
        message.error({
          content: response.message || "Failed to load tender data",
          key: "loadTender",
          duration: 3,
        });
        return;
      }

      // Extract BOQ data from the response
      if (isFallbackRecord) {
        // For BOQ API response
        if (response && response.data) {
          apiData = response.data;
        } else if (response && response.project) {
          apiData = response;
        } else if (
          response &&
          typeof response === "object" &&
          Object.keys(response).length > 0
        ) {
          apiData = response;
        }
      } else {
        // For version API response - extract from 'boq' key
        if (response && response.boq) {
          apiData = response.boq;
        } else if (response && response.data) {
          apiData = response.data;
        } else if (response && response.project) {
          apiData = response;
        } else if (
          response &&
          typeof response === "object" &&
          Object.keys(response).length > 0
        ) {
          apiData = response;
        }
      }

      if (!apiData) {
        message.error({
          content: "No tender data found",
          key: "loadTender",
          duration: 3,
        });
        return;
      }

      const tenderState = transformFromPayload(apiData, {
        id: record.tenderId,
      });

      let hasRoadsAndSewersData = false;
      let hasGroundworksData = false;

      if (
        apiData.roadAndSewers ||
        apiData.preliminaryItems ||
        apiData.foulSewers ||
        apiData.stormSewers ||
        apiData.roads ||
        apiData.mainServices
      ) {
        hasRoadsAndSewersData = true;
      }

      if (
        apiData.groundworks ||
        (apiData.cutFills && Object.keys(apiData.cutFills).length > 0) ||
        (apiData.foundations && Object.keys(apiData.foundations).length > 0)
      ) {
        hasGroundworksData = true;
      }

      const apiJobTypesReview = (apiData as Record<string, unknown>)?.['job-types'] as { groundworks?: boolean; roadsAndSewers?: boolean } | undefined;
      const hasExplicitFlagsReview = apiJobTypesReview?.groundworks !== undefined || apiJobTypesReview?.roadsAndSewers !== undefined;
      tenderState.jobTypes = hasExplicitFlagsReview
        ? { roadsAndSewers: apiJobTypesReview?.roadsAndSewers === true, groundworks: apiJobTypesReview?.groundworks === true }
        : { roadsAndSewers: hasRoadsAndSewersData || record.rsBudget > 0, groundworks: hasGroundworksData || record.gwBudget > 0 };

      // IMPORTANT: Save to localStorage BEFORE dispatching events
      // This ensures that when versionDataReloaded event fires, localStorage has the new data
      const storageKey = `mjEvans_tender_${record.tenderId}`;
      const dataToStore = {
        version: "3.0",
        timestamp: new Date().toISOString(),
        data: apiData,
        versionInfo: {
          version: record.version,
          projectName: record.tender,
        },
      };

      localStorage.setItem(storageKey, JSON.stringify(dataToStore));
      localStorage.setItem("currentTender", JSON.stringify(dataToStore));
      localStorage.setItem(
        "currentBoqVersion",
        JSON.stringify({
          version: record.version,
          projectName: record.tender,
        }),
      );

      // Now dispatch to context - localStorage is already updated
      dispatch({ type: "LOAD_PROJECT", payload: tenderState });




      message.success({
        content: "Tender data loaded successfully",
        key: "loadTender",
        duration: 2,
      });

      // Determine if this is the latest/current version for this record's own
      // job type (same logic as the chip - see latestVersionByPrefix)
      const recordPrefix = getBoqVersionPrefixFromValue(record.version) ?? "__none__";
      const isLatestVersion =
        getNumericBoqVersion(record.version) === latestVersionByPrefix.get(recordPrefix);


      // Set historical flag based on chip logic
      // If chip shows "Current" -> NOT historical (show buttons)
      // If chip shows "Historical" -> IS historical (hide buttons)
      const isHistorical = !isLatestVersion;

      if (isHistorical) {
        setViewMode(true);
        localStorage.setItem("isHistoricalVersion", "true");
      } else {
        setViewMode(false);
        localStorage.setItem("isHistoricalVersion", "false");
      }

      // Navigate to the section (GWKS/RS table) the Review icon was clicked from
      if (section === "GWKS") {
        navigate("/groundworks/review");
      } else {
        navigate("/roads-sewers/review");
      }
    } catch (error) {
      message.error({
        content: "Failed to load tender data",
        key: "loadTender",
        duration: 3,
      });
    }
  };

  const normalizeStatus = (status: string) => {
    const statusLower = status.toLowerCase();
    if (statusLower === "draft") {
      return "In-progress";
    }
    return statusLower;
  };

  const getStatusColor = (status: string) => {
    const statusLower = (status || "").toLowerCase().trim();

    // New BOQ workflow statuses
    if (statusLower === "in-progress") return "blue";
    if (statusLower === "under review - senior estimator") return "orange";
    if (statusLower === "under review commercial director") return "gold";
    if (statusLower === "under client review") return "green";
    if (statusLower === "won") return "purple";

    // Legacy statuses
    const statusNormalized = normalizeStatus(status);
    switch (statusNormalized) {
      case "approved":
        return "success";
      case "submitted":
        return "processing";
      case "pending":
        return "warning";
      default:
        return "default";
    }
  };

  const toPascalCase = (str: string) => {
    return str
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  const buildColumns = (section: "GWKS" | "RS") => {
    const allColumns = [
    {
      title: "Version",
      dataIndex: "version",
      key: "version",
      width: 150,
      fixed: "left" as const,
      render: (version: number, record: VersionRecord) => {
        const recordPrefix = getBoqVersionPrefixFromValue(record.version) ?? "__none__";
        const isLatestVersion =
          getNumericBoqVersion(record.version) === latestVersionByPrefix.get(recordPrefix);

        return (
          <Space size="small">
            <span style={{ fontWeight: 600, fontSize: "14px" }}>
              v{getNumericBoqVersion(version)}
            </span>
            {isLatestVersion && (
              <Tag color="#1890ff" style={{ fontWeight: 500 }}>
                Current
              </Tag>
            )}
          </Space>
        );
      },
    },
    {
      title: "Tender",
      dataIndex: "tender",
      key: "tender",
      width: 200,
      ellipsis: true,
      render: (tender: string) => (
        <span style={{ fontWeight: 500 }}>{tender}</span>
      ),
    },
    {
      title: "Client",
      dataIndex: "client",
      key: "client",
      width: 150,
      ellipsis: true,
    },
    {
      title: "R&S Budget",
      dataIndex: "rsBudget",
      key: "rsBudget",
      width: 130,
      align: "right" as const,
      render: (value: number) => (
        <span style={{ fontWeight: 500 }}>
          £
          {value.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
        </span>
      ),
    },
    {
      title: "GW Budget",
      dataIndex: "gwBudget",
      key: "gwBudget",
      width: 130,
      align: "right" as const,
      render: (value: number) => (
        <span style={{ fontWeight: 500 }}>
          £
          {value.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
        </span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 250,
      render: (status: string, record: VersionRecord) => {
        // Check if this is a historical version for this record's own job
        // type (GWKS/RS sequences are independent - see latestVersionByPrefix)
        const recordPrefix = getBoqVersionPrefixFromValue(record.version) ?? "__none__";
        const isLatestVersion =
          getNumericBoqVersion(record.version) === latestVersionByPrefix.get(recordPrefix);

        // Use grey color for historical versions, normal color for current version
        const statusColor = isLatestVersion ? getStatusColor(status) : "default";

        return (
          <Tag
            color={statusColor}
            style={{ fontWeight: 600, fontSize: "13px" }}
          >
            {toPascalCase(normalizeStatus(status))}
          </Tag>
        );
      },
    },
    {
      title: "Created",
      dataIndex: "creationDate",
      key: "creationDate",
      width: 100,
      render: (date: string) => (
        <span style={{ fontSize: "13px" }}>
          {new Date(date).toLocaleDateString()}
        </span>
      ),
    },
    {
      title: "Last Modified",
      dataIndex: "lastUpdateDate",
      key: "lastUpdateDate",
      width: 110,
      render: (date: string) => (
        <span style={{ fontSize: "13px" }}>
          {new Date(date).toLocaleDateString()}
        </span>
      ),
    },
    {
      title: "Created By",
      dataIndex: "creator",
      key: "creator",
      width: 140,
      render: (creator: string) => (
        <span style={{ fontWeight: 500 }}>{creator ? toPascalCase(creator) : "-"}</span>
      ),
    },
    {
      title: "Last Modified By",
      dataIndex: "lastModifiedBy",
      key: "lastModifiedBy",
      width: 140,
      render: (lastModifiedBy: string) => (
        <span style={{ fontWeight: 500 }}>{toPascalCase(lastModifiedBy)}</span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right" as const,
      width: 180,
      render: (_: any, record: VersionRecord) => {
        const statusLower = (record.status || "").toLowerCase().trim();
        const isWon = statusLower === "won";

        // Check if this is the latest version *for this record's own job
        // type* (GWKS/RS sequences are independent - see latestVersionByPrefix).
        const recordPrefix = getBoqVersionPrefixFromValue(record.version) ?? "__none__";
        const isLatestVersion =
          getNumericBoqVersion(record.version) === latestVersionByPrefix.get(recordPrefix);

        // For previous versions, only show view icon
        if (!isLatestVersion) {
          return (
            <Space size="small">
              <Button
                type="link"
                icon={<EyeOutlined />}
                onClick={() => handleView(record, section)}
                size="small"
                title="View Tender"
              ></Button>
            </Space>
          );
        }

        // For latest version, show all actions with current functionality
        // Commercial Director can now edit tenders
        const isCommercialDirector = userRole?.toLowerCase() === "commercial director";

        // Role + status matrix for Edit button visibility — an In-progress
        // BOQ is always editable regardless of role (per direct request);
        // every other status still goes through the role-specific escalation
        // matrix below.
        const role = (userRole || "").toLowerCase().trim();
        const canEdit = (() => {
          if (isWon) return false;
          if (statusLower === "in-progress") return true;
          if (role === "senior estimator") return statusLower === "under review - senior estimator";
          if (role === "commercial director") return statusLower === "under review commercial director" || statusLower === "under client review";
          return false;
        })();

        return (
          <Space size="small">
            {canEdit && (
              <Button
                type="link"
                icon={<EditOutlined />}
                onClick={() => handleEditClick(record)}
                size="small"
                title="Edit Tender"
              ></Button>
            )}
            <Button
              type="link"
              icon={<EyeOutlined />}
              onClick={() => handleView(record, section)}
              size="small"
              title="View Tender"
            ></Button>
            <Button
              type="link"
              icon={<CalculatorOutlined />}
              onClick={() => handleReviewCalculate(record, section)}
              size="small"
              title="Calculate & Review"
            ></Button>
          </Space>
        );
      },
    },
    ];
    return allColumns.filter((col) => {
      if (section === "GWKS" && col.key === "rsBudget") return false;
      if (section === "RS" && col.key === "gwBudget") return false;
      return true;
    });
  };

  const groundworksColumns = buildColumns("GWKS");
  const roadsAndSewersColumns = buildColumns("RS");

  // Filter version data based on status
  const filteredVersionData = useMemo(() => {
    return versionData.filter((version) => {
      // Status filter (case-insensitive comparison)
      const matchesStatus =
        statusFilter === "all" ||
        version.status.toLowerCase() === statusFilter.toLowerCase();

      return matchesStatus;
    });
  }, [versionData, statusFilter]);

  // Groundworks and Roads & Sewers are shown in separate tables (independent
  // GWKS-N/RS-N sequences). Legacy rows with no prefix (pre-dating this
  // feature) are classified by which budget they actually carry; if both (or
  // neither) are present, the row is ambiguous and shown in both tables.
  const classifyVersionPrefix = (record: VersionRecord): "GWKS" | "RS" | "BOTH" => {
    const detected = getBoqVersionPrefixFromValue(record.version);
    if (detected) return detected;
    const hasGw = record.gwBudget > 0;
    const hasRs = record.rsBudget > 0;
    if (hasGw && !hasRs) return "GWKS";
    if (hasRs && !hasGw) return "RS";
    return "BOTH";
  };

  const groundworksVersionData = useMemo(
    () => filteredVersionData.filter((v) => classifyVersionPrefix(v) !== "RS"),
    [filteredVersionData],
  );
  const roadsAndSewersVersionData = useMemo(
    () => filteredVersionData.filter((v) => classifyVersionPrefix(v) !== "GWKS"),
    [filteredVersionData],
  );

  // Whether each job type has ANY version at all (unfiltered by status) -
  // used to hide the job-type filter dropdown entirely when only one job type
  // exists for this project (nothing meaningful to filter between).
  const groundworksExists = useMemo(
    () => versionData.some((v) => classifyVersionPrefix(v) !== "RS"),
    [versionData],
  );
  const roadsAndSewersExists = useMemo(
    () => versionData.some((v) => classifyVersionPrefix(v) !== "GWKS"),
    [versionData],
  );

  // Summary cards (version count + latest budget per job type) always reflect
  // ALL versions regardless of the status/job-type filters above - a filter
  // narrowing the table shouldn't also change the summary's meaning.
  const groundworksAllVersions = useMemo(
    () => versionData.filter((v) => classifyVersionPrefix(v) !== "RS"),
    [versionData],
  );
  const roadsAndSewersAllVersions = useMemo(
    () => versionData.filter((v) => classifyVersionPrefix(v) !== "GWKS"),
    [versionData],
  );
  const groundworksLatest = useMemo(
    () =>
      groundworksAllVersions.reduce<VersionRecord | null>(
        (latest, v) =>
          !latest || getNumericBoqVersion(v.version) > getNumericBoqVersion(latest.version)
            ? v
            : latest,
        null,
      ),
    [groundworksAllVersions],
  );
  const roadsAndSewersLatest = useMemo(
    () =>
      roadsAndSewersAllVersions.reduce<VersionRecord | null>(
        (latest, v) =>
          !latest || getNumericBoqVersion(v.version) > getNumericBoqVersion(latest.version)
            ? v
            : latest,
        null,
      ),
    [roadsAndSewersAllVersions],
  );

  // Groundworks and Roads & Sewers keep independent GWKS-N/RS-N version
  // sequences, so "latest version" (which drives Edit/Calculate&Review
  // visibility below) must be computed per prefix - not against
  // projectMetadata.currentVersion, which is a single project-wide value that
  // only ever matches one row across the whole table, hiding actions on every
  // other job type's actual latest version.
  const latestVersionByPrefix = useMemo(() => {
    const map = new Map<string, number>();
    versionData.forEach((v) => {
      const prefix = getBoqVersionPrefixFromValue(v.version) ?? "__none__";
      const num = getNumericBoqVersion(v.version);
      if (!map.has(prefix) || num > map.get(prefix)!) {
        map.set(prefix, num);
      }
    });
    return map;
  }, [versionData]);

  // Check if Groundworks / Roads & Sewers each have any "In-Progress"
  // version - tracked per job type (rather than one combined flag) so the
  // Import Take-off button, and which job type's import UI it navigates to,
  // are independent per job type on a dual-job-type tender.
  const hasGroundworksInProgress = useMemo(
    () =>
      groundworksAllVersions.some(
        (version) => version.status.toLowerCase() === "in-progress",
      ),
    [groundworksAllVersions],
  );
  const hasRoadsAndSewersInProgress = useMemo(
    () =>
      roadsAndSewersAllVersions.some(
        (version) => version.status.toLowerCase() === "in-progress",
      ),
    [roadsAndSewersAllVersions],
  );

  // Handlers for the per-job-type "Import Take-off" buttons - each loads that
  // job type's own latest version in edit mode and navigates to
  // import-takeoff scoped to just that job type (`jobType` query param),
  // so a dual-job-type tender doesn't also show the other job type's import
  // card when only one of them was clicked.
  const handleImportTakeoffGroundworks = async () => {
    if (!groundworksLatest) {
      message.error("No Groundworks version data available");
      return;
    }
    await handleEdit(groundworksLatest, "/tender/import-takeoff?jobType=groundworks");
  };

  const handleImportTakeoffRoadsAndSewers = async () => {
    if (!roadsAndSewersLatest) {
      message.error("No Roads & Sewers version data available");
      return;
    }
    await handleEdit(roadsAndSewersLatest, "/tender/import-takeoff?jobType=roadsAndSewers");
  };

  // Handler for Link Opportunity button (single-job-type projects only) -
  // loads latest version in edit mode and navigates to job-types so the
  // missing job type's opportunity can be linked.
  const handleLinkOpportunity = async () => {
    if (!versionData || versionData.length === 0) {
      message.error("No version data available");
      return;
    }

    const latestRecord = versionData[0];
    await handleEdit(latestRecord, "/tender/job-types");
  };

  return (
    <Layout style={{ minHeight: "100vh" }}>
      {/* Navigation Bar */}
      <AntHeader
        className="bg-colorPrimary"
        style={{
          boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
          padding: "12px 24px",
          height: "88px",
          display: "flex",
          alignItems: "center",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
            height: "100%",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "16px",
            }}
          >
            <img
              src={MJ_LOGO}
              alt="M&J Evans Logo"
              onClick={() => navigate("/opportunities")}
              style={{
                height: "64px",
                width: "auto",
                objectFit: "contain",
                cursor: "pointer",
              }}
            />
            <div
              style={{
                width: "1px",
                height: "48px",
                backgroundColor: "rgba(255, 255, 255, 0.3)",
              }}
            />
            <Title
              level={3}
              style={{
                margin: 0,
                color: "white",
                fontWeight: 600,
                fontSize: "20px",
              }}
            >
              Version History
            </Title>
            <EnvironmentBadge mode={import.meta.env.MODE} variant="dark" />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <AuthButton />
          </div>
        </div>
      </AntHeader>

      {/* Main Content */}
      <div style={{ padding: "24px", backgroundColor: "#f5f5f5" }}>
        <Card
          title={
            <Space>
              <Button
                type="text"
                icon={<ArrowLeftOutlined />}
                onClick={() => navigate("/opportunities")}
              >
                Back to Dashboard
              </Button>
              <span
                style={{
                  marginLeft: "16px",
                  fontSize: "18px",
                  fontWeight: 600,
                }}
              >
                Version History - {decodedProjectName}
              </span>
            </Space>
          }
          style={{ borderRadius: "8px" }}
        >
          {/* Filters */}
          <Row gutter={16} style={{ marginBottom: "16px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Col xs={24} sm={12} md={8}>
              <Select
                placeholder="Filter by status"
                value={statusFilter}
                onChange={setStatusFilter}
                style={{ width: "100%" }}
              >
                <Select.Option value="all">All Status</Select.Option>
                <Select.Option value="In-progress">In-Progress</Select.Option>
                <Select.Option value="Under Review - Senior Estimator">
                  Under Review - Senior Estimator
                </Select.Option>
                <Select.Option value="Under Review Commercial Director">
                  Under Review Commercial Director
                </Select.Option>
                <Select.Option value="Under Client Review">
                  Under Client Review
                </Select.Option>
                <Select.Option value="Won">Won</Select.Option>
              </Select>
            </Col>
            {groundworksExists && roadsAndSewersExists && (
              <Col xs={24} sm={12} md={8}>
                <Select
                  placeholder="Filter by job type"
                  value={jobTypeFilter}
                  onChange={setJobTypeFilter}
                  style={{ width: "100%" }}
                >
                  <Select.Option value="all">All Job Types</Select.Option>
                  <Select.Option value="GWKS">Groundworks</Select.Option>
                  <Select.Option value="RS">Roads & Sewers</Select.Option>
                </Select>
              </Col>
            )}
            {devMode && groundworksExists !== roadsAndSewersExists && (
              <Col>
                <Button
                  type="primary"
                  icon={<ImportOutlined />}
                  onClick={handleLinkOpportunity}
                  style={{
                    backgroundColor: "#1890ff",
                    borderColor: "#1890ff",
                  }}
                >
                  Link Opportunity
                </Button>
              </Col>
            )}
          </Row>

          {/* Summary: overall total budget + version count per job type */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 16, marginBottom: 24 }}>
            <StatTile
              icon={<DollarCircleOutlined />}
              color="#1890ff"
              label="Total Budget"
              value={`£${(
                (groundworksExists ? groundworksLatest?.gwBudget ?? 0 : 0) +
                (roadsAndSewersExists ? roadsAndSewersLatest?.rsBudget ?? 0 : 0)
              ).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
              hint="Current version"
            />
            {groundworksExists && (
              <StatTile
                icon={<Layers3 size={20} />}
                color="#52c41a"
                label="Groundworks Versions"
                value={groundworksAllVersions.length}
              />
            )}
            {roadsAndSewersExists && (
              <StatTile
                icon={<Route size={20} />}
                color="#fa8c16"
                label="Roads & Sewers Versions"
                value={roadsAndSewersAllVersions.length}
              />
            )}
          </div>

          {(jobTypeFilter === "all" || jobTypeFilter === "GWKS") && groundworksVersionData.length > 0 && (
            <>
              {(hasGroundworksInProgress || (groundworksExists && roadsAndSewersExists)) && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 12,
                  }}
                >
                  {groundworksExists && roadsAndSewersExists ? (
                    <Title level={5} style={{ margin: 0, display: "flex", alignItems: "center", gap: 8 }}><Layers3 size={18} />Groundworks</Title>
                  ) : (
                    <span />
                  )}
                  {hasGroundworksInProgress && (
                    <Button
                      type="primary"
                      icon={<ImportOutlined />}
                      onClick={handleImportTakeoffGroundworks}
                      style={{
                        backgroundColor: "#1890ff",
                        borderColor: "#1890ff",
                      }}
                    >
                      Import Take-off
                    </Button>
                  )}
                </div>
              )}
              <Table
                columns={groundworksColumns}
                dataSource={groundworksVersionData}
                loading={loading || boqLoading}
                pagination={{
                  pageSize: 10,
                  showSizeChanger: true,
                  showTotal: (total) =>
                    `Total ${total} version${total !== 1 ? "s" : ""}`,
                  pageSizeOptions: ["10", "20", "50"],
                }}
                scroll={{ x: 1880 }}
                size="middle"
                bordered
                style={{ marginBottom: 32 }}
              />
            </>
          )}

          {(jobTypeFilter === "all" || jobTypeFilter === "RS") && roadsAndSewersVersionData.length > 0 && (
            <>
              {(hasRoadsAndSewersInProgress || (groundworksExists && roadsAndSewersExists)) && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 12,
                  }}
                >
                  {groundworksExists && roadsAndSewersExists ? (
                    <Title level={5} style={{ margin: 0, display: "flex", alignItems: "center", gap: 8 }}><Route size={18} />Roads & Sewers</Title>
                  ) : (
                    <span />
                  )}
                  {hasRoadsAndSewersInProgress && (
                    <Button
                      type="primary"
                      icon={<ImportOutlined />}
                      onClick={handleImportTakeoffRoadsAndSewers}
                      style={{
                        backgroundColor: "#1890ff",
                        borderColor: "#1890ff",
                      }}
                    >
                      Import Take-off
                    </Button>
                  )}
                </div>
              )}
              <Table
                columns={roadsAndSewersColumns}
                dataSource={roadsAndSewersVersionData}
                loading={loading || boqLoading}
                pagination={{
                  pageSize: 10,
                  showSizeChanger: true,
                  showTotal: (total) =>
                    `Total ${total} version${total !== 1 ? "s" : ""}`,
                  pageSizeOptions: ["10", "20", "50"],
                }}
                scroll={{ x: 1880 }}
                size="middle"
                bordered
              />
            </>
          )}
        </Card>
      </div>
      <SendTenderReviewModal
        visible={isEditConfirmModalVisible}
        onConfirm={handleEditConfirm}
        onCancel={() => {
          setIsEditConfirmModalVisible(false);
          setEditConfirmRecord(null);
        }}
        loading={isCreatingVersion}
        title="Change to In-Progress"
        message={
          editConfirmRecord
            ? `The Tender Status is "${editConfirmRecord.status}". Are you sure you want to make changes to this tender? Doing so will change the Status from ${editConfirmRecord.status} to In-Progress. Do you want to continue?`
            : ""
        }
        confirmButtonText="Change to In-Progress"
      />
    </Layout>
  );
};

export default VersionHistory;
